import React, { Component } from 'react';
import { connect } from 'react-redux';
import Sizeable from "react-sizeable";
import Classnames from 'classnames';
import { createStructuredSelector } from "reselect";

import BarGraph from '../components/BarGraph'
import BarGraphHoriz from "../components/BarGraphHoriz";
import Radio from "../components/Radio";
import EditableName from '../components/EditableName'
import Checkbox from '../components/Checkbox'
import Column from '../components/Column'

import stressTest from '../actions/query-api/stress-test'
import { retrievePDF } from "../actions/save-to-pdf";

import './styles.css'

const getBasicTestsCrossRef = basicTests => 
  Object.values(
    basicTests
      .map(d => 
        Object.keys(d.factorShifts)
          .reduce((t,v) => ({ ...t, ...(d.factorShifts[v] ? { [v]: d.title } : {}) }), {})
      )
      .reduce((t,v) => {
        let ret = { ...t }
        Object.keys(v).forEach(k => {
          ret[k] = [ ...(ret[k] || []), v[k] ]
        })
        return ret
      }, {})
  )
  .reduce((t,v) => {
    let ret = { ...t }
    v.forEach(k => {
      ret[k] = new Set(...(ret[k] || []), v)
      ret[k].delete(k)
    })
    return ret
  }, {})

class CustomStressTest extends Component {

  constructor(props){
    super(props);
    this.state = {
      hidden: true,
      savedState: true,
      basicTestsChecked: {},
      basicTestsCrossRef: props.basicTests.length 
        ? getBasicTestsCrossRef(props.basicTests) 
        : {},
    }
    this.onSliderChange = this.onSliderChange.bind(this)
    this.onPropChange = this.onPropChange.bind(this)
    this.onMenuSelect = this.onMenuSelect.bind(this)
    this.onSave = this.onSave.bind(this)
    this.onDeleteTest = this.onDeleteTest.bind(this)
    this.onBasicCheck = this.onBasicCheck.bind(this)
    this.onResetFactors = this.onResetFactors.bind(this)
  }

  componentWillReceiveProps(nextProps){
    const { selected, tests, basicTests, open } = nextProps;
    if((!tests[selected].needsSaving || tests[selected].permanent) !== this.state.savedState){    
      this.setState({ savedState: !tests[selected].needsSaving || tests[selected].permanent })
    }
    if(this.props.basicTests.length !== basicTests.length){
      this.setState({ basicTestsCrossRef: getBasicTestsCrossRef(basicTests) })
    }
    if(open && !this.props.open){ // opening
      this.setState({ hidden: false })
    } else if (!open && this.props.open) { // closing
      setTimeout(() => {
        this.setState({ hidden: true })
      },500)
    }
  }

  onSliderChange(factor, value){
    this.props.setFactor(factor, value)
  }

  onPropChange(prop, value){
    this.props.setProp(prop, value)
  }

  onMenuSelect(index){
    let template = this.props.tests[index]

    // don't retain unsaved tests when a new test is selected
    // maybe find a better way to do this
    if(index > 0 && this.props.tests[0].needsSaving){ 
      this.props.deleteTest(null, 0);
      index--;
    }

    if(template.permanent){
      let name = template.name === "New scenario" 
        ? "Unnamed new scenario"
        : "Unnamed scenario ("+template.name+")"
      this.props.addTest({ ...template, name, id: null })
      this.props.selectTest(0)
    } else {
      this.props.selectTest(index)
    }
  }

  onSave(){
    this.props.saveTests(this.props.tests[this.props.selected])
    this.props.retrievePDF();
  }

  onDeleteTest(index){
    this.props.deleteTest(this.props.tests[index].id, index)
  }

  onBasicCheck(event, factorShifts, name){
    this.props.setFactors(
      Object.entries(factorShifts)
        .filter(t => t[0] !== "date" && !!t[1])
        .reduce((t,v) => ({
          ...t,
          [v[0]]: +event*v[1]
        }), {})
    )
    this.setState({
      basicTestsChecked: {
        ...this.state.basicTestsChecked,
        ...Array.from(this.state.basicTestsCrossRef[name]).reduce((t,v) => ({ ...t, [v]: false }), {}),
        [name]: event
      }
    })
  }

  onResetFactors(){
    this.setState({ basicTestsChecked: {} })
    this.props.resetFactors()
  }

  render(){
    const { 
      data,
      portfolios, 
      tests, 
      selected,
      basic,
      basicTests,
      columns,
      colorScale,
      rates,
      TEXT,
      columnLabels,
      TEST_INFO,
      originalPortfolioLabel,
      proposedPortfolioLabel,
    } = this.props

    const { open, setBasic, setOpen } = this.props
    
    const { hidden, savedState, basicTestsChecked } = this.state

    const menu = tests.map(({ name, title, permanent, pending, flagFake }, i) => ({ value: i, label: name, permanent, pending, flagFake }));

    return (<div className={Classnames("custom-stress-test stress-test", { closed: !open })}>
        <div className="section-header">
          <h3>
            <span>{TEXT.customStressTestsLabel}</span>
            <div 
              className={Classnames("toggle-open-close", { closed: !open })}
              onClick={() => setOpen(!open) }>
              <span className="click-to">
                {open ? TEXT.clickToCollapseLabel : TEXT.clickToExpandLabel}
              </span>
            </div>
          </h3>
        </div>
        <div className={Classnames({ "section-body": true, closed: !open, hidden })}>
          <div className="custom-stress-test-pdf-only">{tests[selected].name}</div>
          <div className="control">
            <div className="editable-name-container horizontal">
              <EditableName 
                TEXT={TEXT}
                value={tests[selected].name} 
                onChange={value => this.onPropChange('name', value)} 
                version="name" 
                displayName={
                  <Radio
                    type="dropdown"
                    values={menu}
                    selected={menu[selected]}
                    onChange={this.onMenuSelect}
                    deleteCallback={this.onDeleteTest} />
                }/>
            </div>
            <div className="button-container horizontal">
              <button 
                className={savedState ? "saved" : "unsaved"}
                onClick={this.onSave}>
                <span className="not-mobile">{savedState ? TEXT.savedButton : TEXT.saveRunPDFButton}</span>
                <span className="mobile">{savedState ? TEXT.savedButton : TEXT.saveRunPDFMobileButton}</span>
              </button>
            </div>
            <div className="small-button-container horizontal">
              <button 
                className={this.state.savedState ? "saved" : "unsaved"}
                onClick={this.onResetFactors}>
                {TEXT.resetButton}
              </button>
            </div>
            <div className="options">
              <Radio
                values={[{ value: "Basic", label: TEXT.basicButton }, { value: "Advanced", label: TEXT.advanceButton }]}
                selected={basic ? "Basic" : "Advanced"}
                onChange={value => {
                  if(value === "Basic") this.onResetFactors()
                  setBasic(value === "Basic")
                }}
              />
            </div>
          </div>
          <div>
            <textarea 
              className="mobile-hide"
              cols={12}
              placeholder={TEXT.addDescriptionLabel}
              value={tests[selected].description 
                ? tests[selected].description 
                : undefined}
              onBlur={event => this.onPropChange('description', event.target.value)}/>
            <div className="custom-stress-test-controls">
              <div className="column-holder">
                {basic && basicTests.map((test, i) => 
                  <div className="checkbox-holder" key={test.name}>
                    <Checkbox 
                      onChange={event => this.onBasicCheck(event, test.factorShifts, test.title)}
                      value={basicTestsChecked[test.title]}
                      label={basicTests[i].description} />
                  </div>)}
                {!basic && Object.keys(columns).map(c => 
                  <Column 
                    key={c}
                    label={TEXT[columnLabels[c]]}
                    data={columns[c]}
                    rates={c === "rates" && rates}
                    portfolios={portfolios}
                    factors={tests[selected].factorShifts}
                    onChange={this.onSliderChange} />)}
              </div>
              <div className="guidance" 
                dangerouslySetInnerHTML={{__html: (basic ? TEXT.basicStressTestDisclosure : TEXT.advancedStressTestDisclosure)
                  .replace("[[ASOFDATE]]", rates["30 Day T-Bill"] ? rates["30 Day T-Bill"].date : "(date unavailable)")
                  .replace("[[LINK]]", TEST_INFO) 
                }}></div>
            </div>
          </div>
          <div className="custom-stress-test-graphs">
            <div className="custom-stress-test-graph">
              <h4>{TEXT.portfolioLabel}</h4>
              <h4 className="subtitle">
                <span className="original">{originalPortfolioLabel ? originalPortfolioLabel : TEXT.originalPortfolioLabel}</span>
                <span className="small"> vs. </span>
                <span className="proposed">{proposedPortfolioLabel ? proposedPortfolioLabel : TEXT.proposedPortfolioLabel}</span>
              </h4>
              <Sizeable>{({ width }) =>
                <BarGraph
                  width={width || 500}
                  data={data.portfolio[selected].values}
                  x="portfolio"
                  y="value"
                  yFormat=".1%"
                  colorScale={colorScale}
                />
              }</Sizeable>
            </div>
            <div className="custom-stress-test-graph">
              <h4>{TEXT.assetsLabel}</h4>
              <h4 className="subtitle">
                <span className="original">{originalPortfolioLabel ? originalPortfolioLabel : TEXT.originalPortfolioLabel}</span>
                <span className="small"> vs. </span>
                <span className="proposed">{proposedPortfolioLabel ? proposedPortfolioLabel : TEXT.proposedPortfolioLabel}</span>
              </h4>
              <Sizeable>{({ width }) =>
                <BarGraphHoriz
                  width={width || 500}
                  data={data.asset[selected]}
                  x="value"
                  y="key"
                  xFormat=".1%"
                  colorScale={colorScale} />
              }</Sizeable>
            </div>
          </div>
        </div>
      </div>)
  }

}

const mapStateToProps = createStructuredSelector({
  tests: state => state.customStressTests,
  selected: state => state.selectedCustomStressTest,
  basic: state => state.customStressTestIsBasic,
  basicTests: state => state.basicCustomStressTests,
  columns: state => state.customStressTestColumns,
  rates: state => state.riskFreeRates,
  open: state => state.customStressTestIsOpen,
  TEXT: state => state.complianceText,
  columnLabels: state => state.customStressTestColumnLabelsLookup,
});

const mapDispatchToProps = dispatch => ({
  // when saving multiple tests, PUT the ones that have id, POST the ones that lack
  saveTests: test => {
    if(!!test.id){
      dispatch(stressTest('PUT', test, false))
    } else {
      dispatch(stressTest('POST', test))
    }
  },
  deleteTest: (id, index) => {
    if(!id){
      dispatch({ type: 'DELETE_STRESS_TEST', index })
    } else {
      dispatch(stressTest("PUT", { id }, true))
    }
  },
  retrievePDF: () => dispatch(retrievePDF),
  addTest: test => dispatch({ type: "ADD_STRESS_TEST", test }),
  selectTest: index => dispatch({ type: "SELECT_CUSTOM_STRESS_TEST", index }),
  setFactor: (factor, value) => dispatch({ type: 'SET_STRESS_TEST_FACTOR', factor, value }),
  setFactors: factorShifts => dispatch({ type: 'SET_STRESS_TEST_FACTORS', factorShifts }),
  setProp: (prop, value) => dispatch({ type: 'SET_STRESS_TEST_PROP', prop, value }),
	setBasic: basic => dispatch({ type: 'SET_CUSTOM_STRESS_TEST_MODE', basic }),
  resetFactors: () => dispatch({ type: 'RESET_STRESS_TEST_FACTORS' }),
  setOpen: open => dispatch({ type: 'SET_CUSTOM_STRESS_TEST_OPEN', open }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomStressTest);


// WEBPACK FOOTER //
// ./src/CustomStressTest/index.js